import React, { useEffect, useState } from "react";
import { MenuProps, Tooltip } from "antd";
import { Avatar, Dropdown, Layout, Space } from "antd";
import {
  BellOutlined,
  DownOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
  UpOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { usePermissions } from "../../PermissionsProvider";
import { CompanyService } from "../../services/CompanyService";
import { Company } from "../../model_gen/company";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom"; // Added import for useAuth0
import { OutlineButton, BadgeButton } from "./button/Buttons";
import Search from "../Search/Search";
import useStore from "../../store/useStore";
import AlertTray from "../Notifications/AlertTray";
import { NotificationService } from "../../services/NotificationService";
import {
  Notification,
  NotificationStatus,
  NotificationType,
} from "../../model_gen/notification";
import { useWebSocket } from "../../utils/Websockets";

const { Header } = Layout;

const HeaderComponent: React.FC = () => {
  const { apiKey } = usePermissions();
  const navigate = useNavigate();
  const { dataReadyTutorial } = useStore();
  // Company not being used, kept for future implementation
  // // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [company, setCompany] = useState<Company | null>(null);
  const { user, logout } = useAuth0(); // Destructure logout from useAuth0
  const [isHeaderDropDownMenuOpen, setIsHeaderDropDownMenuOpen] =
    useState<boolean>(false);
  const [alertTrayVisible, setAlertTrayVisible] = useState<boolean>(false);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  const items: MenuProps["items"] = [
    {
      label: <a>Account Settings</a>,
      key: "0",
      disabled: true,
    },
    {
      label: <a onClick={() => navigate("/billing")}>Billing</a>,
      key: "1",
    },
    {
      label: <a>Support</a>,
      key: "2",
      disabled: true,
    },
    {
      type: "divider",
    },
    {
      label: (
        <a
          onClick={() =>
            logout({ logoutParams: { returnTo: `${window.location.origin}` } })
          }>
          Logout
        </a>
      ),
      key: "4",
    },
  ];

  useEffect(() => {
    if (apiKey) {
      CompanyService.getCompany(null, apiKey).subscribe(
        (result: Company) => {
          setCompany(result);
        },
        (error: any) => {
          console.error(error);
        }
      );
    }
  }, [apiKey]);

  useEffect(() => {
    if (apiKey) {
      // Fetch unread notifications
      fetchUnreadNotifications();
    }
  }, [apiKey, refreshTrigger]);

  const fetchUnreadNotifications = () => {
    // Only get CREATED status notifications (unread)
    const notificationStatuses: NotificationStatus[] = ["CREATED"];
    // Only include FINETUNING_COMPLETE notifications, exclude FAULT
    const excludeTypes: NotificationType[] = ["FAULT"];
    NotificationService.getNotifications(
      notificationStatuses,
      [],
      excludeTypes,
      null,
      apiKey
    ).subscribe(
      (result: Notification[]) => {
        setUnreadCount(result ? result.length : 0);
      },
      (error: any) => {
        console.error("Error fetching notification count:", error);
      }
    );
  };

  // Handle new notification from websocket
  const handleNewNotification = () => {
    // A new notification arrived via websocket, trigger a refresh
    setRefreshTrigger((prev) => prev + 1);
  };

  // Set up websocket listener for notifications
  useWebSocket({
    key: company?.id,
    eventType: "NOTIFICATION",
    onMessage: handleNewNotification,
  });

  const handleOpenChange = (open: boolean) => {
    setIsHeaderDropDownMenuOpen(open);
  };

  const toggleAlertTray = () => {
    setAlertTrayVisible(!alertTrayVisible);
    if (alertTrayVisible) {
      fetchUnreadNotifications();
    }
  };

  return (
    <Header className="Header main-header-wrap">
      <div className="leftSection">
        {/* <img src={MaitaiLogo} style={{ width: '125px', height: 'auto', marginRight: '80px' }} /> */}
        <Search disabled={!dataReadyTutorial} />
      </div>
      <div
        style={{
          background: "transparent",
          border: "none",
          width: "40%",
          justifyContent: "flex-end",
          display: "flex",
          gap: "20px",
          alignItems: "center",
        }}>
        <OutlineButton
          className="menu-icon-size"
          target="_blank"
          href="https://docs.trymaitai.ai"
          icon={<QuestionCircleOutlined />}
        />

        {/* Using the new BadgeButton component */}
        <BadgeButton
          className="menu-icon-size"
          onClick={toggleAlertTray}
          icon={<BellOutlined />}
          badgeCount={unreadCount}
          badgePosition={{ top: "7px", right: "-3px" }}
        />

        <Tooltip
          title={
            !dataReadyTutorial
              ? "Make a request using the Maitai SDK to view this page"
              : ""
          }>
          <OutlineButton
            className="menu-icon-size"
            onClick={() => navigate("/settings")}
            icon={<SettingOutlined />}
            disabled={!dataReadyTutorial}
          />
        </Tooltip>
        <Dropdown
          menu={{ items }}
          trigger={["click"]}
          onOpenChange={handleOpenChange}>
          <a onClick={(e) => e.preventDefault()}>
            <Space>
              {user?.picture ? (
                <Avatar size={32} src={user?.picture} />
              ) : (
                <UserOutlined />
              )}
              {isHeaderDropDownMenuOpen ? <UpOutlined /> : <DownOutlined />}
            </Space>
          </a>
        </Dropdown>
      </div>

      {/* Notifications Drawer */}
      <AlertTray
        visible={alertTrayVisible}
        onClose={() => setAlertTrayVisible(false)}
        refreshTrigger={refreshTrigger}
        onNotificationsRead={fetchUnreadNotifications}
      />
    </Header>
  );
};

export default HeaderComponent;
